/**
 * useContextView.ts
 *
 * Hook for fetching a context view configuration from schema-service.
 */

import { useState, useEffect, useRef } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';
import { getContextView, type ContextViewRecord } from '../api/contextApi';

/** Return value of the useContextView hook */
export interface UseContextViewReturn {
  view: ContextViewRecord | null;
  loading: boolean;
  error: string | null;
  /**
   * HTTP status of the failed fetch, when available — lets a caller
   * distinguish a resolution failure (403/404: the view is gone or
   * unreadable) from a transient outage (503: SpiceDB unreachable,
   * `getContextViewBySystemName` deliberately returns 503 rather than a
   * refusal). `SharedLinkBannerMolecule` needs this split (EAINT-793 D-4) —
   * folding 503 into the 403/404 message would tell a shared-link recipient
   * "unavailable to you" during an outage that has nothing to do with them.
   */
  errorStatus: number | undefined;
  /**
   * True while a *background* refetch is in flight — a `refreshKey` bump for a
   * view whose identity did not change. Distinct from `loading`, which stays
   * false on that path precisely so the caller keeps rendering what it has.
   * Without a separate signal the refetch is invisible: `TreeMapOrganism`'s
   * D-4 retry would leave the user clicking a button that appears to do
   * nothing (EAINT-793 R-7). Callers that never pass a `refreshKey` never see
   * this go true.
   */
  refreshing: boolean;
}

/**
 * Fetches a context view config by system name.
 *
 * @param apiClients - API clients (pass null if not ready)
 * @param viewSystemName - View system name to fetch
 * @param refreshKey - Bump to force a refetch when the view record changed
 *   server-side under an UNCHANGED `viewSystemName`. Saving an edit in the view
 *   editor is exactly that case: the name is the same, so without this the
 *   effect never refires and the caller keeps a stale `base_config` — which
 *   `updateContextView` then replaces wholesale on the next write. EAINT-789.
 */
export function useContextView(
  apiClients: ApiClients | null,
  viewSystemName: string | undefined,
  refreshKey = 0
): UseContextViewReturn {
  const [view, setView] = useState<ContextViewRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);
  const [refreshing, setRefreshing] = useState(false);

  const isMountedRef = useRef(true);
  /**
   * `system_name` of the record currently held in `view`. A ref, not `view`
   * itself: reading `view` inside the fetch effect would make it a dependency,
   * and the effect writes it — an unconditional refetch loop.
   */
  const loadedSystemNameRef = useRef<string | null>(null);
  /**
   * What the effect last ATTEMPTED, and the key it attempted it under — as
   * opposed to `loadedSystemNameRef`, which records only what actually landed.
   * The distinction is the whole of EAINT-793 R-7: after a failed load nothing
   * landed, so a "do we already hold this view?" test cannot tell a retry of the
   * same view from a switch to a new one.
   */
  const requestedSystemNameRef = useRef<string | undefined>(undefined);
  const prevRefreshKeyRef = useRef(refreshKey);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // No view selected (e.g. switched to a raw context) — clear any previously
    // loaded view so the caller falls back to built-in defaults instead of
    // rendering the stale view.
    if (!apiClients || !viewSystemName) {
      setView(null);
      loadedSystemNameRef.current = null;
      // Forget the attempt too, so whatever is selected next is a FOREGROUND
      // load. Leaving the name here would let "no view → same view again" be
      // mistaken for a background refetch and skip the spinner on a genuine
      // load.
      requestedSystemNameRef.current = undefined;
      prevRefreshKeyRef.current = refreshKey;
      setRefreshing(false);
      setError(null);
      setErrorStatus(undefined);
      // Settle `loading` here too. It is only ever cleared in the `.finally`
      // below, which is gated on `!cancelled` — so clearing the view while a
      // GET is in flight cancels that fetch and strands `loading` at true
      // forever. `TreeMapOrganism` gates the WHOLE board on it, so the board
      // would sit on a full-screen spinner with no way back except reselecting
      // a view. (`handleContextSelect` sets the view to null; the refresh-key
      // refetch added for the wholesale-replace fix widened the window.)
      setLoading(false);
      return;
    }

    let cancelled = false;
    // Only blank the board for a load that has nothing to show. A refetch of
    // the SAME view (the refresh-key bump after an editor save, or D-4's retry)
    // keeps the current screen, so the correctness fix does not cost a
    // full-screen spinner and a remount of the grid, bar and popovers.
    //
    // The test is "the caller asked for a refetch AND the view identity did not
    // change", not "we already hold this view". The latter was the original
    // proxy and it fails in exactly the case the retry lives in (EAINT-793
    // R-7): the previous fetch ERRORED, so `loadedSystemNameRef` is null, the
    // proxy said "different view", and every Retry click blanked the board the
    // user was reading, dropped scroll position, and unmounted the Retry button
    // from under the cursor.
    //
    // Gating on the refresh key rather than on identity alone is what keeps
    // this a no-op for the other seven consumers on a view SWITCH: they all
    // call the two-arg form, so `refreshKey` is a constant 0 and can never
    // differ. A view switch still takes the blanking path below, whose two
    // named consumers depend on it.
    //
    // Precisely, though (EAINT-793 R-21): requiring `refreshKeyChanged` means a
    // two-arg consumer takes the blanking path on ANY effect re-run, not only a
    // view switch — including one caused by an `apiClients` identity change
    // while the same view is loaded, which previously went the background way.
    // `AtlasComponent` (which ignores `loading`) would blank for a tick there.
    // The only trigger today is an account/identity switch, where blanking is
    // the defensible answer anyway, so this is a narrowing of the claim rather
    // than a defect — but the claim is "no-op unless the effect re-runs for a
    // reason other than a view switch", not "no-op, full stop".
    const refreshKeyChanged = refreshKey !== prevRefreshKeyRef.current;
    const sameViewRequested = requestedSystemNameRef.current === viewSystemName;
    prevRefreshKeyRef.current = refreshKey;
    requestedSystemNameRef.current = viewSystemName;

    const isBackgroundRefetch = refreshKeyChanged && sameViewRequested;
    setRefreshing(isBackgroundRefetch);
    if (!isBackgroundRefetch) {
      // Switching to a DIFFERENT view drops the previous record, it does not
      // merely raise `loading` — the mirror of the fail-closed-BEFORE-the-fetch
      // rule the permission hooks state for exactly this reason. Two consumers
      // make it load-bearing rather than cosmetic: `AtlasComponent`
      // destructures `{ view }` and ignores `loading`, so it would render the
      // previous view's `view_config` under the new name; and
      // `commitPendingSharedFilters` writes to `view.system_name`, so a shared
      // filter would land in the PREVIOUS view's org-wide `base_config` while
      // the switcher shows the new one. Clearing here also covers the error
      // path: a failed switch leaves `view` null rather than resurrecting the
      // old record with `loading: false`.
      setView(null);
      loadedSystemNameRef.current = null;
      setLoading(true);
      // Clear the error only on the path that also clears the view. Holding it
      // across a background refetch is what keeps the retry's own banner — and
      // therefore the Retry button the user just pressed — mounted while the
      // attempt is in flight; dropping it here would send the notice to `none`
      // and back, remounting the control and losing focus (R-7). The success
      // and failure handlers below settle it either way, so nothing can strand.
      setError(null);
      setErrorStatus(undefined);
    }

    getContextView(apiClients.schemaClient, viewSystemName)
      .then((result) => {
        if (!cancelled && isMountedRef.current) {
          setView(result);
          loadedSystemNameRef.current = result?.system_name ?? null;
          setError(null);
          setErrorStatus(undefined);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && isMountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to fetch context view');
          const status =
            (err as { status?: number })?.status ?? (err as { response?: { status?: number } })?.response?.status;
          setErrorStatus(status);
        }
      })
      .finally(() => {
        if (!cancelled && isMountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiClients, viewSystemName, refreshKey]);

  return { view, loading, error, errorStatus, refreshing };
}
